import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Layers, ChevronDown, Check } from 'lucide-react';
import { toast } from 'sonner';
import { PRODUCTION_TOOLS, resolveToolRoute } from '@/lib/productionTools';

/**
 * Universal Production Tools dropdown — one component, reused in the
 * header of all nine Production Tools, so navigation between them never
 * has to go back through the Production Hub.
 *
 * Props:
 *  - currentTool: id of the tool rendering this menu (one of
 *    PRODUCTION_TOOLS' ids) — highlighted, and not itself clickable.
 *  - creatorId / projectId / assetId / returnTo: current context,
 *    forwarded to resolveToolRoute(). Pass whatever the calling page
 *    already knows; unknown ones are simply omitted from the target URL.
 *  - onBeforeNavigate: optional async function called (and awaited)
 *    before navigating away. Editor/Clips pass their existing
 *    `flushAutosave` here so a pending autosave is always written before
 *    switching tools — no invented "unsaved changes" dialog is needed
 *    because the real save already happens automatically. If it throws
 *    (the save itself failed), navigation is cancelled and an error
 *    toast is shown instead of silently leaving the tool with unsaved
 *    work. Tools with no such mechanism simply omit this prop.
 */
export function ProductionToolsDropdown({ currentTool, creatorId, projectId, assetId, returnTo, onBeforeNavigate }) {
  const navigate = useNavigate();
  const ctx = { creatorId, projectId, assetId, returnTo };

  const handleSelect = async (toolId) => {
    if (toolId === currentTool) return;
    const path = resolveToolRoute(toolId, ctx);
    if (!path) return;
    if (onBeforeNavigate) {
      try {
        await onBeforeNavigate();
      } catch {
        toast.error('Your changes could not be saved, so navigation was cancelled. Please try again before switching tools.');
        return;
      }
    }
    navigate(path);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1.5 text-xs font-medium text-[#C0C7D4] hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg px-3 py-1.5 transition-colors focus:outline-none focus:ring-1 focus:ring-[#3A7BFF]"
          data-testid="production-tools-dropdown-trigger"
        >
          <Layers className="h-3.5 w-3.5" />
          Production Tools
          <ChevronDown className="h-3 w-3 opacity-70" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-[#14171F] border-white/10 w-56" data-testid="production-tools-dropdown-content">
        {PRODUCTION_TOOLS.map((tool) => {
          const path = resolveToolRoute(tool.id, ctx);
          const available = !!path;
          const isCurrent = tool.id === currentTool;
          const Icon = tool.icon;
          return (
            <DropdownMenuItem
              key={tool.id}
              disabled={!available || isCurrent}
              onSelect={(e) => { e.preventDefault(); handleSelect(tool.id); }}
              className={isCurrent ? 'bg-[#3A7BFF]/15 text-white focus:bg-[#3A7BFF]/20 focus:text-white' : !available ? 'opacity-40 cursor-not-allowed' : ''}
              title={!available ? 'Open from inside a project first' : undefined}
              aria-current={isCurrent ? 'page' : undefined}
              data-testid={`production-tools-item-${tool.id}`}
            >
              <Icon className="h-4 w-4 mr-2 shrink-0" />
              <span className="flex-1 truncate">{tool.label}</span>
              {isCurrent && <Check className="h-3.5 w-3.5 shrink-0" data-testid={`production-tools-current-${tool.id}`} />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
